import { createTheme } from '@mui/material/styles';
import { customPaletteOptions } from './light';
import { QcIssueStatus } from '../../interfaces/qcIssue.interface';
import { ACCENT_BLUE, ACCENT_BLUE_DARK, SUCCESS_GREEN, ERROR_RED, WARN_ORANGE, FIRST_PASS_PURPLE } from '../Assessments/Workspace/qcTheme';

// Route-scoped theme for the workspace, applied via nested ThemeProvider only under /assessment/:assessmentId.

// Design-handoff tokens that fall outside MUI palette slots.
export const WORKSPACE_COLORS = {
  pageBackground: '#f3f5fb',
  hairline: 'rgba(0,0,0,0.08)',
  actionBlue: ACCENT_BLUE,
  actionBlueDark: ACCENT_BLUE_DARK,
  danger: ERROR_RED,
  firstPassPurple: FIRST_PASS_PURPLE,
  bpRosette: '#D467E4',
  scopingAmber: WARN_ORANGE,
  mutedText: 'rgba(0,0,0,0.45)',
  titleGrey: '#424242',
  filterPillBg: '#eef4fc',
  filterPillBorder: 'rgba(25,118,210,0.25)',
  breadcrumbHover: 'rgba(25,118,210,0.08)',
};

// Status colors from the handoff (used consistently everywhere).
export const QC_STATUS_COLORS: Record<QcIssueStatus, { main: string; text: string }> = {
  first_pass: { main: '#8a8f98', text: '#5f6368' },
  pending_assessor_action: { main: '#8a8f98', text: '#5f6368' },
  needs_update: { main: WARN_ORANGE, text: WARN_ORANGE },
  ready_for_tpn_review: { main: ACCENT_BLUE_DARK, text: ACCENT_BLUE_DARK },
  approved: { main: SUCCESS_GREEN, text: SUCCESS_GREEN },
};

export const ASSESSOR_ANSWER_COLORS: Record<string, { rgb: string; text: string }> = {
  'Fully Implemented': { rgb: '46,125,50', text: SUCCESS_GREEN },
  'Partially Implemented': { rgb: '168,107,0', text: '#a86b00' },
  'Not Implemented': { rgb: '211,47,47', text: ERROR_RED },
  'Not Applicable': { rgb: '97,97,97', text: '#616161' },
};

export const assessmentTheme = createTheme({
  palette: {
    mode: 'light',
    ...customPaletteOptions,
    primary: {
      main: WORKSPACE_COLORS.actionBlue,
      dark: WORKSPACE_COLORS.actionBlueDark,
    },
    error: {
      main: WORKSPACE_COLORS.danger,
    },
    background: {
      default: WORKSPACE_COLORS.pageBackground,
      paper: '#ffffff',
    },
    divider: WORKSPACE_COLORS.hairline,
    text: {
      primary: 'rgba(0,0,0,0.87)',
      secondary: WORKSPACE_COLORS.mutedText,
    },
  },
  typography: {
    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 8,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0,0,0,0.87)',
          },
          '&.Mui-focused': {
            boxShadow: `inset 0 0 0 1px ${WORKSPACE_COLORS.actionBlue}`,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 'unset',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.72rem',
        },
      },
    },
  },
});
